import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Award, Users, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';

const APropos = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: CheckCircle2,
      title: 'Qualité & Précision',
      description: 'Finitions impeccables et attention aux détails'
    },
    {
      icon: Users,
      title: 'Transparence',
      description: 'Devis clairs et communication constante'
    },
    {
      icon: Wrench,
      title: 'Expertise',
      description: 'Artisans qualifiés et expérimentés'
    },
    {
      icon: Award,
      title: 'Engagement',
      description: 'Respect des délais et chantier propre'
    }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-14 sm:py-18 lg:py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-6"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2B2B2B]"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              À propos
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Rénovation de Salle de Bains est un service spécialisé en rénovation premium
              de salles de bains en Suisse romande.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-14 sm:py-18 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Notre mission
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Nous accompagnons les propriétaires en Suisse romande dans la transformation
                  de leur salle de bains en un espace moderne, fonctionnel et durable.
                </p>
                <p>
                  Notre approche clé en main vous garantit un projet sans stress, de la
                  conception à la livraison, avec des finitions impeccables et un respect
                  total de vos attentes.
                </p>
                <p>
                  Grâce à notre service de plans 3D, vous pouvez visualiser votre projet
                  avant de vous engager, et ainsi valider les grandes orientations esthétiques
                  et fonctionnelles en toute sérénité.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1558211583-03ed8a0b3d5f?w=800" 
                alt="Salle de bains moderne"
                className="rounded-xl shadow-xl"
              />
            </motion.div>
          </div>

          {/* Values */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Nos valeurs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="h-full border-slate-200/70 hover:shadow-lg transition-shadow duration-200">
                    <CardContent className="p-6 space-y-3 text-center">
                      <div className="w-12 h-12 rounded-lg bg-[#336DBF]/10 flex items-center justify-center mx-auto">
                        <value.icon className="w-6 h-6 text-[#336DBF]" />
                      </div>
                      <h3 className="font-semibold text-lg text-[#2B2B2B]">{value.title}</h3>
                      <p className="text-sm text-slate-600">{value.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Zone d'intervention */}
          <div className="bg-slate-50 rounded-2xl p-8 sm:p-12">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Zone d'intervention
            </h2>
            <p className="text-center text-lg text-slate-700 mb-8">
              Nous intervenons dans toute la <strong>Suisse romande</strong>:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Genève', 'Lausanne', 'Montreux', 'Valais', 'Fribourg', 'Neuchâtel'].map((city, i) => (
                <span 
                  key={i}
                  className="px-4 py-2 bg-white rounded-full text-slate-700 font-medium border border-slate-200"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>

          {/* Homely Solutions */}
          <div className="mt-20 text-center">
            <Card className="border-slate-200/70 max-w-2xl mx-auto">
              <CardContent className="p-8 space-y-4">
                <h3 className="text-2xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Une marque Homely Solutions
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  <strong>Rénovation de Salle de Bains</strong> est une marque et un service
                  édité et exploité par <strong>Homely Solutions</strong>, spécialiste de la
                  rénovation intérieure en Suisse.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-18 lg:py-24 bg-[#2B2B2B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Prêt à démarrer votre projet?
          </h2>
          <p className="text-xl text-slate-300">
            Obtenez un devis personnalisé et gratuit en quelques minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button 
              onClick={() => navigate('/devis')}
              size="lg"
              className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium px-8 py-6"
            >
              Demander un devis
            </Button>
            <Button 
              onClick={() => navigate('/galerie')}
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#2B2B2B] px-8 py-6"
            >
              Voir nos réalisations
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default APropos;